/**
 * RachioCalibrationVsProbesPage — `/rachio-advanced-settings-vs-soil-probes`
 *
 * Surgical technical receipt for the Bermuda Lawn Dominators thread Alex
 * Rome comment ("Stock rachio accounts for it properly if you calibrate
 * field capacity, evapotranspiration rate, and allowable depletion to your
 * specific grass and soil type"). He's right on paper, wrong in practice.
 *
 * Page goal: ONE definitive URL Justin can drop in any future Facebook /
 * Reddit / Rachio Community thread when someone says "calibration is enough."
 *
 * Voice: respectful, technically deep, never dismissive of Rachio. The
 * argument is calibration ≠ measurement, not "Rachio bad."
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import { FaqJsonLd } from '../components/FaqJsonLd';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  AlertTriangle, CheckCircle2, XCircle, ArrowRight, Calculator,
  Sun, Cloud, Sprout,
} from 'lucide-react';

const URL = 'https://getturfpulse.com/rachio-advanced-settings-vs-soil-probes';

const FAQS = [
  {
    q: "Can Rachio's Advanced Settings actually replicate what soil probes do?",
    a: "Partly — on paper, with perfect calibration of AWC, root depth, depletion threshold, crop coefficient, and efficiency, the flex daily model gets you ~80–90% of the way for a single uniform zone. In practice it fails for three reasons: no ground-truth feedback loop, no micro-climate awareness (shade vs sun zones get the same model), and homeowner calibration drift. Probes don't replace the model — they correct it.",
  },
  {
    q: "What's the real difference between calibrated forecast and measured soil moisture?",
    a: "Calibrated forecast = a one-way model. Rachio computes how much water it thinks evaporated based on weather data, subtracts that from the model's belief about field capacity, and fires when the math says you're below your depletion threshold. Measured soil moisture = ground truth. A WH51 probe reports the actual volumetric water content in your root zone every 15 minutes. The model can be 20–30% wrong and you'd never know without a probe to verify.",
  },
  {
    q: "How much does it cost to add probes vs just calibrating Rachio?",
    a: "Calibrating Rachio's Advanced Settings is free but takes 2–3 hours per property for someone who knows what they're doing — and there's no way to verify if your inputs are right without ground truth. Adding probes: ~$45 for a GW1100 gateway (one-time, covers up to 8 zones), ~$30 per WH51 soil moisture probe. So roughly $75 to start, $150 for full per-zone coverage on a typical 4-zone residential. Either way, getting calibration close on the first try is the harder problem.",
  },
  {
    q: "Does Rachio's flex daily mode use real soil data?",
    a: "No. Flex Daily uses a virtual soil bucket model. It calculates ET₀ from the nearest weather station, subtracts that depletion from your zone's modeled field capacity, and fires when the modeled balance drops below your allowable-depletion threshold. There's a manual `rachio.set_zone_moisture_percent` service in Home Assistant that lets you write a value into Rachio's flex calculation — that's the integration trick irrigation hobbyists use to bridge the gap. TurfPulse uses the same API hook automatically every 15 minutes.",
  },
  {
    q: "When is calibration alone good enough?",
    a: "Three scenarios: (1) Small, flat, single-soil-type lot with one grass species and no shade transitions. (2) You have a soil test report from a lab showing actual AWC and you know your root depth empirically. (3) You're okay being approximately right — you don't need precision, you just want to skip the obvious rain days. For everyone else, especially HOA properties, commercial sites, mixed-shade residential, or anywhere you have to prove water savings, probes earn their cost back in a season.",
  },
  {
    q: "Will TurfPulse work alongside a properly calibrated Rachio?",
    a: "Yes — that's actually the ideal setup. Calibrate Rachio's Advanced Settings as well as you can, then layer TurfPulse on top as the soil-truth override. The two systems don't compete: Rachio's flex daily handles scheduling and pulse logic, TurfPulse vetoes a fire when the probes disagree with the model. Best of both. We don't ask you to undo your Rachio tuning.",
  },
];

const ADVANCED_SETTINGS = [
  { name: 'Available Water Capacity (AWC)', unit: 'in/in', what: 'How much water your soil can hold per inch of depth.', why: 'Sand: ~0.06, loam: ~0.18, clay: ~0.20. Get this wrong by 40% and every other calculation downstream is wrong.' },
  { name: 'Root Depth', unit: 'inches', what: 'How deep the model assumes your grass roots reach.', why: 'Bermuda can be 4-12" depending on mow height + irrigation history. Default 6" is wrong for half of bermuda lawns.' },
  { name: 'Allowed Depletion (MAD)', unit: '%', what: 'How dry the model lets the bucket get before firing.', why: 'Most defaults are 50%. Lower = more frequent shallow waterings. Higher = deeper, less frequent.' },
  { name: 'Crop Coefficient (Kc)', unit: 'dimensionless', what: 'Multiplier on reference ET₀ for your grass species.', why: 'Bermuda: ~0.80. St. Augustine: ~0.85. Tall fescue: ~0.90. Affects how fast the model depletes the bucket.' },
  { name: 'Efficiency', unit: '%', what: 'How much of fired water reaches the root zone.', why: 'Spray heads: ~60%. Rotors: ~70%. MP Rotators: ~85%. Drip: ~90%. Wrong here and you over- or under-water consistently.' },
  { name: 'Soil Type', unit: 'enum', what: 'Drop-down that auto-fills AWC if you skip the custom value.', why: 'Useful default, but the dropdown options are gross simplifications. "Clay loam" covers a 3x range of real AWC.' },
];

const FAILURE_MODES = [
  {
    icon: <Cloud className="w-5 h-5" />,
    title: 'No ground-truth feedback loop',
    summary: 'The model runs forward; it never gets corrected by reality.',
    example: 'Rachio computes 31% depletion after 4 hot days. Your probe says 18%. Rachio fires; you didn\'t need water. Model has no way to learn it was wrong — same error next week.',
    fix: 'Probes inject real volumetric water content into the decision. The model output gets a sanity check before it touches a valve.',
  },
  {
    icon: <Sun className="w-5 h-5" />,
    title: 'Micro-climate blindness',
    summary: 'Identical settings fire identical zones, but identical zones don\'t exist.',
    example: 'Two zones, same bermuda, same soil, same AWC. Zone 2 gets afternoon shade from the house. Real-world depletion difference: 20–35% in July. Rachio fires both equally. Zone 2 ends up overwatered all season.',
    fix: 'Per-zone probes catch the actual depletion gradient. TurfPulse holds zone 2 while firing zone 4.',
  },
  {
    icon: <AlertTriangle className="w-5 h-5" />,
    title: 'Calibration drift + homeowner accuracy',
    summary: '"Just calibrate it properly" assumes data you don\'t have.',
    example: 'Round Rock has clay loam over caliche, often with imported topsoil over native subsoil. The soil triangle is wrong for half the lots. Most homeowners pick the dropdown default and move on. Even pros admit AWC is the hardest field to get right.',
    fix: 'Probes don\'t care what the soil triangle says. They measure what\'s actually there, in your actual yard, today.',
  },
];

export default function RachioCalibrationVsProbesPage() {
  return (
    <div
      className="min-h-screen bg-gradient-to-b from-zinc-50 via-white to-emerald-50/30 dark:from-zinc-950 dark:via-zinc-950 dark:to-emerald-950/10"
      data-testid="rachio-calibration-page"
    >
      <SEO
        title="Rachio's Advanced Settings vs Soil Probes — What Calibration Can and Can't Do"
        description="Honest technical breakdown of every Rachio Advanced Setting (AWC, Root Depth, Allowable Depletion, Crop Coefficient, Efficiency, Soil Type) and the three reasons perfect calibration alone still fails. With concrete numbers and when calibration is enough."
        canonical={URL}
      />
      <BreadcrumbJsonLd
        items={[
          { label: 'Home', path: '/' },
          { label: 'Rachio Advanced Settings vs Soil Probes', path: '/rachio-advanced-settings-vs-soil-probes' },
        ]}
      />
      <FaqJsonLd faqs={FAQS} />

      {/* Hero */}
      <section className="border-b border-border bg-zinc-900 text-zinc-50 dark:bg-zinc-950" data-testid="calibration-hero">
        <div className="max-w-4xl mx-auto px-5 py-14 sm:py-18">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-bold text-emerald-300">
            <Calculator className="w-3.5 h-3.5" /> Technical breakdown
          </div>
          <h1 className="mt-4 text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            Rachio's Advanced Settings:<br />
            <span className="text-emerald-300">what calibration can and can't do.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            "Stock Rachio accounts for it properly if you calibrate field capacity,
            evapotranspiration rate, and allowable depletion to your specific
            grass and soil type." — this is technically correct and practically
            incomplete. Here's exactly where the gap is, with numbers.
          </p>
          <div className="mt-6 flex gap-3 flex-wrap">
            <a href="#failure-modes">
              <Button className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold" data-testid="calibration-hero-cta">
                Jump to the 3 failure modes <ArrowRight className="ml-1 w-4 h-4" />
              </Button>
            </a>
            <Link to="/ecowitt-to-rachio">
              <Button variant="outline" className="border-zinc-600 text-zinc-200 hover:bg-zinc-800 hover:text-zinc-50">
                The full Ecowitt → Rachio breakdown
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* TL;DR */}
      <section className="py-10 sm:py-12 bg-white dark:bg-zinc-950 border-b border-border" data-testid="calibration-tldr">
        <div className="max-w-4xl mx-auto px-5">
          <Card className="border-l-4 border-l-emerald-500 bg-emerald-50/40 dark:bg-emerald-950/20 p-5">
            <h2 className="text-xs uppercase tracking-widest font-bold text-emerald-700 dark:text-emerald-400 mb-2">
              TL;DR
            </h2>
            <p className="text-base sm:text-lg text-zinc-900 dark:text-zinc-50 font-semibold leading-snug">
              Calibration is <em>theoretical</em>. Soil moisture is <em>empirical</em>. A perfectly calibrated forecast-only controller still has no feedback loop — it can be 20–30% wrong and never know. Probes don't replace Rachio's flex model; they correct it.
            </p>
          </Card>
        </div>
      </section>

      {/* Each setting */}
      <section className="py-12 sm:py-14 bg-zinc-50/60 dark:bg-zinc-900/30 border-b border-border" data-testid="calibration-settings">
        <div className="max-w-4xl mx-auto px-5">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 dark:text-zinc-50">
            What each Rachio Advanced Setting actually does
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Every field in the Advanced tab, explained honestly. None of these are wrong — they're just inputs to a model that doesn't see your dirt.
          </p>
          <div className="mt-6 space-y-2">
            {ADVANCED_SETTINGS.map((s, i) => (
              <details
                key={i}
                className="group rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4"
                data-testid={`calibration-setting-${i}`}
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-3">
                  <div>
                    <div className="font-bold text-zinc-900 dark:text-zinc-50 text-sm sm:text-base">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{s.unit}</div>
                  </div>
                  <span className="text-emerald-600 dark:text-emerald-400 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className="mt-3 space-y-1.5 text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
                  <p><strong>What it does:</strong> {s.what}</p>
                  <p><strong>Why it matters:</strong> {s.why}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* The 3 failure modes */}
      <section
        id="failure-modes"
        className="py-12 sm:py-14 bg-white dark:bg-zinc-950 border-b border-border"
        data-testid="calibration-failures"
      >
        <div className="max-w-4xl mx-auto px-5">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 dark:text-zinc-50">
            The 3 places perfect calibration still fails
          </h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            Each of these failure modes applies to <strong>any</strong> forecast-only controller —
            Hydrawise, Rain Bird IQ, Toro Sentinel, OpenSprinkler — not just Rachio.
            They're properties of the calibration approach, not Rachio's implementation.
          </p>
          <div className="mt-8 space-y-5">
            {FAILURE_MODES.map((m, i) => (
              <div
                key={i}
                className="rounded-xl border border-amber-300/60 dark:border-amber-900/40 bg-amber-50/40 dark:bg-amber-950/20 p-5"
                data-testid={`calibration-failure-${i}`}
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 flex items-center justify-center">
                    {m.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-zinc-900 dark:text-zinc-50">{i + 1}. {m.title}</h3>
                    <p className="text-sm text-zinc-700 dark:text-zinc-300 mt-1">{m.summary}</p>
                    <div className="mt-3 rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-3 space-y-1.5 text-sm">
                      <p className="text-zinc-700 dark:text-zinc-300">
                        <strong className="text-amber-700 dark:text-amber-400">Example:</strong> {m.example}
                      </p>
                      <p className="text-zinc-700 dark:text-zinc-300">
                        <strong className="text-emerald-700 dark:text-emerald-400">Probe fix:</strong> {m.fix}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Honest dual section */}
      <section className="py-12 sm:py-14 bg-zinc-50/60 dark:bg-zinc-900/30 border-b border-border" data-testid="calibration-when">
        <div className="max-w-4xl mx-auto px-5">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 dark:text-zinc-50">
            When each approach is the right call
          </h2>
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-emerald-300/60 dark:border-emerald-800/60 bg-emerald-50/40 dark:bg-emerald-950/20 p-5" data-testid="calibration-enough">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                <h3 className="font-bold text-emerald-900 dark:text-emerald-200">Calibration alone is enough</h3>
              </div>
              <ul className="space-y-1.5 text-sm text-zinc-800 dark:text-zinc-200">
                <li>• Small, flat, single-soil-type lot</li>
                <li>• One grass species, no shade transitions</li>
                <li>• You have a real soil-lab AWC report</li>
                <li>• You're okay being approximately right</li>
                <li>• You just want to skip obvious rain days</li>
              </ul>
            </div>
            <div className="rounded-xl border border-amber-300/60 dark:border-amber-800/60 bg-amber-50/40 dark:bg-amber-950/20 p-5" data-testid="calibration-probes-win">
              <div className="flex items-center gap-2 mb-2">
                <Sprout className="w-5 h-5 text-amber-700 dark:text-amber-400" />
                <h3 className="font-bold text-amber-900 dark:text-amber-200">Probes earn their cost</h3>
              </div>
              <ul className="space-y-1.5 text-sm text-zinc-800 dark:text-zinc-200">
                <li>• HOA or commercial water reporting</li>
                <li>• Drought-restricted region (TX, AZ, SoCal)</li>
                <li>• Mixed shade / sun zones</li>
                <li>• Mixed grass species or imported topsoil</li>
                <li>• You actually look at your water bill</li>
                <li>• Want measured proof, not modeled estimates</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-12 sm:py-14 bg-white dark:bg-zinc-950 border-b border-border" data-testid="calibration-vs-table">
        <div className="max-w-4xl mx-auto px-5">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 dark:text-zinc-50">
            Side-by-side: calibrated Rachio vs probe-driven override
          </h2>
          <div className="mt-6 overflow-x-auto rounded-xl border border-zinc-200 dark:border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 dark:bg-zinc-900 text-left">
                <tr>
                  <th className="px-4 py-3 font-bold text-zinc-700 dark:text-zinc-300"></th>
                  <th className="px-4 py-3 font-bold text-zinc-700 dark:text-zinc-300">Calibrated Rachio</th>
                  <th className="px-4 py-3 font-bold text-emerald-700 dark:text-emerald-400">+ Probes (TurfPulse)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
                {[
                  ['Forecast skip on rain', 'Yes', 'Yes (Rachio handles this)'],
                  ['Per-zone moisture awareness', 'No — model only', 'Yes — real probe per zone'],
                  ['Detect model drift', 'No', 'Yes — flagged when probe disagrees ≥15%'],
                  ['Micro-climate compensation', 'No', 'Yes — auto per probe'],
                  ['Drought-floor override', 'Manual', 'Auto via TAMU 12% threshold'],
                  ['Dollar-spot pressure flag', 'No', 'Yes — Smith-Kerns model'],
                  ['Setup time', '2–3 hours (per zone tune)', '5 min one-time'],
                  ['Cost', 'Free (your time)', '~$75–$150 hardware + free tier or $4.99/mo'],
                  ['Ongoing maintenance', 'Re-tune seasonally', 'Fully automated'],
                  ['Verifiable savings', 'Estimated', 'Per-zone measured deltas'],
                ].map(([label, a, b], i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white dark:bg-zinc-950' : 'bg-zinc-50/50 dark:bg-zinc-900/30'}>
                    <td className="px-4 py-3 font-semibold text-zinc-900 dark:text-zinc-50">{label}</td>
                    <td className="px-4 py-3 text-zinc-700 dark:text-zinc-300">{a}</td>
                    <td className="px-4 py-3 text-emerald-800 dark:text-emerald-300 font-medium">{b}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Not anti-Rachio. The probe stack literally fires through Rachio's API
            and respects every Advanced Setting you configured. It adds a
            real-soil sanity check, nothing else.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-14 bg-zinc-50/60 dark:bg-zinc-900/30 border-b border-border" data-testid="calibration-faq">
        <div className="max-w-3xl mx-auto px-5">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 dark:text-zinc-50 mb-6">
            Honest answers to the technical pushback
          </h2>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <details
                key={i}
                className="group rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4"
                data-testid={`calibration-faq-${i}`}
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-3 font-semibold text-zinc-900 dark:text-zinc-50">
                  <span>{f.q}</span>
                  <span className="text-emerald-600 dark:text-emerald-400 group-open:rotate-45 transition-transform shrink-0">+</span>
                </summary>
                <p className="mt-3 text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-emerald-950 dark:bg-emerald-950 text-zinc-50" data-testid="calibration-cta">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight">
            Want a public side-by-side test?
          </h2>
          <p className="mt-3 text-base text-emerald-100 max-w-2xl mx-auto">
            Genuinely open invitation: pick a property, calibrate stock Rachio
            as well as you can, run a TurfPulse-equipped property next door,
            and we'll publish the deltas — whoever wins. Email
            <a href="mailto:justin@getturfpulse.com" className="font-bold underline ml-1">justin@getturfpulse.com</a>.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/ecowitt-to-rachio">
              <Button className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold" data-testid="calibration-cta-primary">
                See the full Ecowitt → Rachio path <ArrowRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/for-contractors">
              <Button variant="outline" className="border-emerald-400/40 text-emerald-100 hover:bg-emerald-900">
                Reading this as a pro?
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
